import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';

/**
 * Storage Service that provides a unified API for data storage
 * - Uses Capacitor Preferences for native apps (iOS/Android)
 * - Falls back to localStorage for web browsers
 * - Uses IndexedDB for large data sets (userResults, etc.)
 * 
 * Benefits:
 * - Capacitor Preferences: More reliable on mobile, uses native storage (UserDefaults/SharedPreferences)
 * - IndexedDB: Much larger storage limits (50%+ of disk space vs 5-10MB for localStorage)
 * - Automatic fallback ensures compatibility with both web and native
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly isNative = Capacitor.isNativePlatform();
  private readonly INDEXED_DB_NAME = 'tatoolbox_db';
  private readonly INDEXED_DB_VERSION = 2;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isInitialized = false;

  constructor(
    private logger: LoggerService,
    private errorHandler: ErrorHandlerService
  ) {
    // Initialize IndexedDB for large data storage
    if (typeof indexedDB !== 'undefined') {
      this.initPromise = this.initIndexedDB();
    }
  }

  /**
   * Initialize IndexedDB for large data storage
   */
  private async initIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.INDEXED_DB_NAME, this.INDEXED_DB_VERSION);
      
      request.onerror = () => {
        this.logger.warn('IndexedDB initialization failed, will use fallback storage');
        this.isInitialized = true; // Mark as initialized even on error to prevent retries
        resolve(); // Don't fail, just use fallback
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        
        // Handle database close event
        this.db.onclose = () => {
          this.logger.warn('IndexedDB connection closed, will use fallback storage');
          this.db = null;
        };
        
        // Handle database error event
        this.db.onerror = (event) => {
          this.logger.warn('IndexedDB error:', event);
        };
        
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object stores for large data
        if (!db.objectStoreNames.contains('userResults')) {
          db.createObjectStore('userResults', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('companyUsers')) {
          db.createObjectStore('companyUsers', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('statusSnapshot')) {
          db.createObjectStore('statusSnapshot', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Ensure IndexedDB is initialized before use
   */
  private async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    if (this.initPromise) {
      await this.initPromise;
    } else if (typeof indexedDB !== 'undefined') {
      this.initPromise = this.initIndexedDB();
      await this.initPromise;
    }
  }

  /**
   * Set a value in storage (small to medium data)
   * 
   * Uses Capacitor Preferences on native platforms (iOS/Android) for better reliability,
   * falls back to localStorage on web browsers. Automatically handles JSON serialization.
   * 
   * @param key - Storage key identifier
   * @param value - Value to store (will be JSON stringified if not a string)
   * @returns Promise resolving to true if successful, false if quota exceeded or error occurred
   * 
   * @throws Will log error via ErrorHandlerService but returns false instead of throwing
   * 
   * @example
   * ```typescript
   * await storageService.set('userData', { name: 'John', role: 'admin' });
   * await storageService.set('auth_secret', 'token123');
   * ```
   */
  async set(key: string, value: any): Promise<boolean> {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      
      if (this.isNative) {
        // Use Capacitor Preferences on native platforms
        await Preferences.set({ key, value: stringValue });
        return true;
      } else {
        // Use localStorage on web
        localStorage.setItem(key, stringValue);
        return true;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.logger.warn(`Storage quota exceeded for key "${key}". Consider using setLarge() for large data.`);
        return false;
      }
      this.errorHandler.handleStorageError('set', key, error);
      return false;
    }
  }

  /**
   * Get a value from storage
   */
  async get(key: string): Promise<string | null> {
    try {
      if (this.isNative) {
        const result = await Preferences.get({ key });
        return result.value;
      } else {
        return localStorage.getItem(key);
      }
    } catch (error) {
      this.errorHandler.handleStorageError('get', key, error);
      return null;
    }
  }

  /**
   * Get a value and parse it as JSON
   */
  async getJSON<T>(key: string): Promise<T | null> {
    const value = await this.get(key);
    if (!value) return null;
    
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      this.errorHandler.handleStorageError('parse', key, error);
      return null;
    }
  }

  /**
   * Remove a value from storage
   */
  async remove(key: string): Promise<boolean> {
    try {
      if (this.isNative) {
        await Preferences.remove({ key });
      } else {
        localStorage.removeItem(key);
      }
      return true;
    } catch (error) {
      this.errorHandler.handleStorageError('remove', key, error);
      return false;
    }
  }

  /**
   * Clear all storage
   */
  async clear(): Promise<boolean> {
    try {
      if (this.isNative) {
        await Preferences.clear();
      } else {
        localStorage.clear();
      }
      // Also clear IndexedDB
      await this.clearLarge('userResults');
      await this.clearLarge('companyUsers');
      return true;
    } catch (error) {
      this.errorHandler.handleStorageError('clear', 'all', error);
      return false;
    }
  }

  /**
   * Store large data in IndexedDB (for userResults, companyUsers, etc.)
   * Falls back to localStorage if IndexedDB is not available
   */
  async setLarge(storeName: string, data: any): Promise<boolean> {
    try {
      // Ensure IndexedDB is initialized before use
      await this.ensureInitialized();
      
      // Check if database is available and connection is open
      if (this.db && this.db.objectStoreNames.contains(storeName)) {
        // Check if database connection is still open
        try {
          // Use IndexedDB
          return new Promise((resolve) => {
            try {
              const transaction = this.db!.transaction([storeName], 'readwrite');
              const store = transaction.objectStore(storeName);
              
              const request = store.put({ id: storeName, data, timestamp: Date.now() });
              
              request.onsuccess = () => resolve(true);
              request.onerror = () => {
                this.logger.warn(`IndexedDB write failed for ${storeName}, falling back to localStorage`);
                resolve(this.fallbackToLocalStorage(storeName, data));
              };
              
              transaction.onerror = () => {
                this.logger.warn(`IndexedDB transaction error for ${storeName}, falling back to localStorage`);
                resolve(this.fallbackToLocalStorage(storeName, data));
              };
            } catch (transactionError) {
              // Database connection might be closed
              this.logger.warn(`IndexedDB transaction creation failed for ${storeName}, falling back to localStorage:`, transactionError);
              this.db = null; // Reset db reference
              resolve(this.fallbackToLocalStorage(storeName, data));
            }
          });
        } catch (error) {
          // Database connection is closed or invalid
          this.logger.warn(`IndexedDB connection invalid for ${storeName}, falling back to localStorage:`, error);
          this.db = null; // Reset db reference
          return this.fallbackToLocalStorage(storeName, data);
        }
      } else {
        // Fallback to localStorage
        return this.fallbackToLocalStorage(storeName, data);
      }
    } catch (error) {
      this.errorHandler.handleStorageError('setLarge', storeName, error);
      return this.fallbackToLocalStorage(storeName, data);
    }
  }

  /**
   * Get large data from IndexedDB
   * Falls back to localStorage if IndexedDB is not available
   */
  async getLarge<T>(storeName: string): Promise<T | null> {
    try {
      // Ensure IndexedDB is initialized before use
      await this.ensureInitialized();
      
      // Check if database is available and connection is open
      if (this.db && this.db.objectStoreNames.contains(storeName)) {
        try {
          // Use IndexedDB
          return new Promise((resolve) => {
            try {
              const transaction = this.db!.transaction([storeName], 'readonly');
              const store = transaction.objectStore(storeName);
              const request = store.get(storeName);
              
              request.onsuccess = () => {
                const result = request.result;
                if (result && result.data) {
                  resolve(result.data as T);
                } else {
                  // Fallback to localStorage
                  resolve(this.fallbackFromLocalStorage<T>(storeName));
                }
              };
              
              request.onerror = () => {
                this.logger.warn(`IndexedDB read failed for ${storeName}, falling back to localStorage`);
                resolve(this.fallbackFromLocalStorage<T>(storeName));
              };
              
              transaction.onerror = () => {
                this.logger.warn(`IndexedDB transaction error for ${storeName}, falling back to localStorage`);
                resolve(this.fallbackFromLocalStorage<T>(storeName));
              };
            } catch (transactionError) {
              // Database connection might be closed
              this.logger.warn(`IndexedDB transaction creation failed for ${storeName}, falling back to localStorage:`, transactionError);
              this.db = null; // Reset db reference
              resolve(this.fallbackFromLocalStorage<T>(storeName));
            }
          });
        } catch (error) {
          // Database connection is closed or invalid
          this.logger.warn(`IndexedDB connection invalid for ${storeName}, falling back to localStorage:`, error);
          this.db = null; // Reset db reference
          return this.fallbackFromLocalStorage<T>(storeName);
        }
      } else {
        // Fallback to localStorage
        return this.fallbackFromLocalStorage<T>(storeName);
      }
    } catch (error) {
      this.errorHandler.handleStorageError('getLarge', storeName, error);
      return this.fallbackFromLocalStorage<T>(storeName);
    }
  }

  /**
   * Remove large data from IndexedDB
   */
  async removeLarge(storeName: string): Promise<boolean> {
    try {
      // Ensure IndexedDB is initialized before use
      await this.ensureInitialized();
      
      if (this.db && this.db.objectStoreNames.contains(storeName)) {
        try {
          return new Promise((resolve) => {
            try {
              const transaction = this.db!.transaction([storeName], 'readwrite');
              const store = transaction.objectStore(storeName);
              const request = store.delete(storeName);
              
              request.onsuccess = () => {
                // Also remove from localStorage fallback
                localStorage.removeItem(storeName);
                resolve(true);
              };
              request.onerror = () => {
                localStorage.removeItem(storeName);
                resolve(false);
              };
              transaction.onerror = () => {
                localStorage.removeItem(storeName);
                resolve(false);
              };
            } catch (transactionError) {
              this.logger.warn(`IndexedDB transaction creation failed for removeLarge ${storeName}, using localStorage:`, transactionError);
              this.db = null;
              localStorage.removeItem(storeName);
              resolve(true);
            }
          });
        } catch (error) {
          this.logger.warn(`IndexedDB connection invalid for removeLarge ${storeName}, using localStorage:`, error);
          this.db = null;
          localStorage.removeItem(storeName);
          return true;
        }
      } else {
        localStorage.removeItem(storeName);
        return true;
      }
    } catch (error) {
      this.errorHandler.handleStorageError('removeLarge', storeName, error);
      localStorage.removeItem(storeName);
      return false;
    }
  }

  /**
   * Clear all data from a specific IndexedDB store
   */
  async clearLarge(storeName: string): Promise<boolean> {
    try {
      // Ensure IndexedDB is initialized before use
      await this.ensureInitialized();
      
      if (this.db && this.db.objectStoreNames.contains(storeName)) {
        try {
          return new Promise((resolve) => {
            try {
              const transaction = this.db!.transaction([storeName], 'readwrite');
              const store = transaction.objectStore(storeName);
              const request = store.clear();
              
              request.onsuccess = () => {
                localStorage.removeItem(storeName);
                resolve(true);
              };
              request.onerror = () => {
                localStorage.removeItem(storeName);
                resolve(false);
              };
              transaction.onerror = () => {
                localStorage.removeItem(storeName);
                resolve(false);
              };
            } catch (transactionError) {
              this.logger.warn(`IndexedDB transaction creation failed for clearLarge ${storeName}, using localStorage:`, transactionError);
              this.db = null;
              localStorage.removeItem(storeName);
              resolve(true);
            }
          });
        } catch (error) {
          this.logger.warn(`IndexedDB connection invalid for clearLarge ${storeName}, using localStorage:`, error);
          this.db = null;
          localStorage.removeItem(storeName);
          return true;
        }
      } else {
        localStorage.removeItem(storeName);
        return true;
      }
    } catch (error) {
      this.errorHandler.handleStorageError('clearLarge', storeName, error);
      localStorage.removeItem(storeName);
      return false;
    }
  }

  /**
   * Fallback to localStorage for large data (with quota handling)
   */
  private fallbackToLocalStorage(key: string, data: any): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.logger.warn(`localStorage quota exceeded for ${key}. Data may be too large.`);
        // Try to free space
        const largeKeys = ['userResults', 'tatUserDataByProduct', 'companyUsers', 'companyUsersActivationState'];
        for (const largeKey of largeKeys) {
          if (largeKey !== key) {
            try {
              localStorage.removeItem(largeKey);
            } catch (e) {
              // Ignore
            }
          }
        }
        // Retry
        try {
          localStorage.setItem(key, JSON.stringify(data));
          return true;
        } catch (retryError) {
          this.errorHandler.handleStorageError('setLarge-retry', key, retryError);
          return false;
        }
      }
      return false;
    }
  }

  /**
   * Fallback from localStorage for large data
   */
  private fallbackFromLocalStorage<T>(key: string): T | null {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;
      return JSON.parse(value) as T;
    } catch (error) {
      this.errorHandler.handleStorageError('get', key, error);
      return null;
    }
  }

  /**
   * Check if storage is available
   */
  isAvailable(): boolean {
    if (this.isNative) {
      return true; // Capacitor Preferences is always available
    }
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage estimate (approximate)
   */
  async getStorageUsage(): Promise<{ used: number; available: number }> {
    // This is a rough estimate
    let used = 0;
    
    if (this.isNative) {
      // For native, we can't easily calculate, return estimate
      return { used: 0, available: 50 * 1024 * 1024 }; // Assume 50MB available
    }
    
    // Calculate localStorage usage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }
    }
    
    // Estimate available (typically 5-10MB for localStorage)
    const available = 10 * 1024 * 1024 - used; // 10MB - used
    
    return { used, available: Math.max(0, available) };
  }
}
